import { AssetType, CustomTheme, EmploymentStatus, getAgentById, Loan, ProfessionalDetails } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { ProfessionalDetailsForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/components/ProfessionalDetailsForm';
import { EMPLOYMENT_STATUS_FILTER } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/constants';
import {
  ProfessionalDetailsFormSchema,
  ProfessionalDetailsFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/schemas/ProfessionalDetailsFormSchema';
import { seniorityDateFormatter } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/utils/seniorityDateToMonthsAndYears';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCache } from '@/hooks/requests/useCache';

interface ProfessionalDetailsEditDialogProps {
  professionalDetails: ProfessionalDetails;
  loan: Loan;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  toggleDialog: () => void;
  updateBorrowerContract?: (data) => void;
  isCompanyCifRequired?: boolean;
}

export const ProfessionalDetailsEditDialog = ({
  professionalDetails,
  loan,
  opened,
  setOpened,
  updateBorrowerContract,
  loading,
  toggleDialog,
  isCompanyCifRequired = false,
}: ProfessionalDetailsEditDialogProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { data: agent } = useCache(getAgentById, { agentId: loan.agentId });

  const professionalDetailsFormDefaultValues = {
    employmentStatus: professionalDetails.employmentStatus,
    professionalPosition: professionalDetails.professionalPosition,
    companyName: professionalDetails.companyInfo?.name ?? null,
    companyActivity: professionalDetails.companyInfo?.activity ?? null,
    companyCity: professionalDetails.companyInfo?.city ?? null,
    seniority: seniorityDateFormatter.monthsAndYearsToDate(
      professionalDetails.companyInfo?.seniorityMonths,
      professionalDetails.companyInfo?.seniorityYears,
    ),
    contractDateEnd: professionalDetails.companyInfo?.contractDateEnd ?? null,
    corporateIdNumber: professionalDetails.companyInfo?.corporateIdNumber ?? null,
    selfEmploymentNetIncome: professionalDetails.selfEmploymentNetIncome?.value?.amount
      ? String(professionalDetails.selfEmploymentNetIncome.value.amount)
      : null,
    selfEmploymentNetIncomeDate: professionalDetails.selfEmploymentNetIncome?.date ?? null,
  };

  const professionalDetailsPreMapper = (useFormValues: ProfessionalDetailsFormType) => {
    const seniority = seniorityDateFormatter.dateToMonthsAndYears(useFormValues.seniority);

    return {
      employmentStatus: useFormValues.employmentStatus,
      ...(EMPLOYMENT_STATUS_FILTER.find((element) => element === useFormValues.employmentStatus)
        ? {
            companyInfo: {
              activity: useFormValues.companyActivity,
              name: useFormValues.companyName,
              city: useFormValues.companyCity,
              seniorityMonths: seniority?.seniorityMonths,
              seniorityYears: seniority?.seniorityYears,
              companyContractDateEnd: useFormValues?.contractDateEnd ?? null,
              corporateIdNumber: useFormValues?.corporateIdNumber ?? null,
            },
            professionalPosition: useFormValues.professionalPosition,
          }
        : {}),
      ...(useFormValues.employmentStatus === EmploymentStatus.SELF_EMPLOYED &&
      useFormValues.selfEmploymentNetIncome &&
      useFormValues.selfEmploymentNetIncomeDate
        ? {
            selfEmploymentNetIncome: {
              value: {
                amount: Number(useFormValues.selfEmploymentNetIncome),
                currency: loan.loanConditions.currency,
              },
              date: useFormValues.selfEmploymentNetIncomeDate,
            },
          }
        : {}),
    };
  };

  const onSubmitForm = async (useFormValues: ProfessionalDetailsFormType) => {
    if (updateBorrowerContract) {
      const preMappedProfessionalDetailsValues = professionalDetailsPreMapper(useFormValues);
      await updateBorrowerContract({ userDetails: { professionalDetails: preMappedProfessionalDetailsValues } });
    }
    toggleDialog();
  };

  if (!agent) return null;

  const additionalProfessionalDetailsRequired =
    agent.customTheme !== CustomTheme.CAIXABANK && loan.assetReference[0].type === AssetType.AUTO;

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_PROFESSIONAL_DETAILS'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      defaultValues={professionalDetailsFormDefaultValues}
      formSchema={() =>
        ProfessionalDetailsFormSchema(
          getTextIdTranslation,
          additionalProfessionalDetailsRequired,
          isCompanyCifRequired,
        )
      }
    >
      <ProfessionalDetailsForm
        professionalDetails={professionalDetails}
        additionalProfessionalDetailsRequired={additionalProfessionalDetailsRequired}
        isCompanyCifRequired={isCompanyCifRequired}
        fullWidth={true}
        fieldVariant={FieldVariant.form}
      />
    </EditDialogWrapper>
  );
};
